<template>
	<div @click.prevent="onNotesClick(order)" class="cursor-pointer mb-2">
		<div
			:class="
				`flex justify-between items-center w-full  border-p-light rounded-t-lg shadow-sm pl-1 ${
					!isManagingOneBusiness ? 'border-t-1' : ''
				} ${isRemainingTimeCritical ? 'border-l-1 border-r-1 border-red-600' : 'border-l-1 border-r-1'}`
			"
		>
			<div v-if="!isManagingOneBusiness" class="flex flex-col content-center">
				<span class="text-16 font-bold">{{ order.restaurant.restaurantName }}</span>
				<span class="text-11 text-gray-500"> {{ order.description }}</span>
			</div>
			<app-tooltip v-if="order.errorMsg" :text="order.errorMsg">
				<app-icon icon="attention" class="text-primary animate-ping mr-1" />
			</app-tooltip>
		</div>
		<div :class="statusCardStyle">
			<div class="flex flex-col ml-1 p-1 border-r-1 border-p-light">
				<span class="text-muted">Number</span>
				<div class="font-semibold text-2xl" @click.stop="routeToOrderDetails">#{{ formatOrderId }}</div>
				<div
					v-if="order['paymentMethod'].paymentType !== 'CASH'"
					class="flex gap-2 justify-start place-items-center"
				>
					<app-tooltip text="Credit Card" v-if="order['paymentMethod'].paymentType === 'CREDIT_CARD'">
						<img alt="credit-card" class="w-5 h-auto" src="../../assets/images/credit-card.svg" />
					</app-tooltip>
					<span v-else>{{ order['paymentMethod'].paymentType }}</span>
				</div>
				<span
					v-if="order.restaurant.withWaiters && order.waiter.id === -1"
					class="cursor-pointer hover:underline text-11 text-blue-600 "
					@click.stop="showWaitersModal"
				>
					{{ 'Add Waiter' }}
				</span>
				<span
					v-if="order.waiter.id !== -1 && !(order.status === 'DELIVERED' || order.status === 'REJECTED')"
					@click="showWaitersModal"
					class="text-11 text-red-500 hover:underline text-right cursor-pointer"
				>
					({{ order.waiter.name }} {{ order.waiter.surname }})</span
				>
				<span
					v-if="order.waiter.id !== -1 && (order.status === 'DELIVERED' || order.status === 'REJECTED')"
					class="text-11 text-red-500 text-right"
				>
					({{ order.waiter.name }} {{ order.waiter.surname }})</span
				>
			</div>
			<div class="flex-1 flex flex-col gap-2 p-1">
				<div class="flex justify-between">
					<div class="flex-1 flex flex-col">
						<div class="flex gap-2">
							<span class="text-muted">Value</span>
						</div>
						<span class="font-semibold text-2xl whitespace-nowrap">{{
							$filters.formatCurrency(order.total, order.currency)
						}}</span>
					</div>
					<div class="flex flex-row items-center gap-1">
						<CountdownTimer
							v-if="
								order['enableCookingTime'] &&
									(order['status'] === statuses.pending ||
										order['status'] === statuses.accepted ||
										order['status'] === statuses.pickedUp)
							"
							:acceptedTime="
								order['status'] === statuses.pending
									? order['lastDatePending']
									: order['lastDateAccepted']
							"
							:readyTime="
								order['status'] === statuses.pending
									? posSettings.maxPendingMinutes
									: order.totalCookingTime
							"
						/>
					</div>
				</div>
				<order-status-progress-bar :order-status="order.status" />
			</div>
			<order-status-icon
				:status="order.status"
				:is-takeaway="order.isTakeAwayOrder"
				:loading="isLoading"
				:enable-cooking-time-prop="order['enableCookingTime']"
				@answer="$emit('answer', $event)"
				@cookOrder="$emit('cookOrder', $event)"
				@deliverOrder="$emit('deliverOrder', $event)"
			/>
		</div>
		<div
			v-if="isRemainingTimeCritical"
			:class="
				`flex justify-between items-center w-full  border-p-light rounded-b-lg shadow-sm pl-1 ${
					!isManagingOneBusiness ? 'border-b-1' : ''
				} ${isRemainingTimeCritical ? 'border-l-2 border-r-2 border-b-2 border-red-600' : ''}`
			"
		>
			<div class="flex items-center w-full">
				<app-icon icon="clock" class="text-red-600 mr-1" />
				<span class="text-16 italic self-center">{{ $t('time_up') }}</span>
			</div>
		</div>
	</div>
	<app-modal v-if="waitersModal" @close="waitersModal = false" :title="'Add Waiter'">
		<div class="flex flex-col gap-4">
			<span class="text-12 text-muted"> *Select waiter than click save to assign waiter. </span>
			<div class="flex flex-col gap-2" v-for="waiter in waitersList" :key="waiter.id">
				<button
					class="text-2xl border-1 border-gray-400 hover:bg-gray-200 active:bg-green-700 focus:outline-none focus:ring focus:ring-green-300 focus:bg-gray-200 p-2"
					@click="selectedWaiter = waiter.id"
				>
					{{ waiter.name }} {{ waiter.surname }}
				</button>
			</div>
			<app-button
				class="btn btn-success w-full wt-2"
				@click="assignWaiterToOrder(this.selectedWaiter)"
				type="submit"
				:loading="loading"
				>Save</app-button
			>
		</div>
	</app-modal>
</template>

<script>
	import moment from 'moment';
	import OrderService from '@/services/OrderService';
	import orders from '@/constants/orders';
	import names from '@/router/names';
	import OrderStatusProgressBar from '@/components/orders/OrderStatusProgressBar';
	import OrderStatusIcon from '@/components/orders/OrderStatusAction';
	import { mapGetters } from 'vuex';
	import UtilityService from '@/services/UtilityService';
	import AppModal from '@/components/app/AppModal';
	import RouteNames from '@/router/names';
	import mapOrderStatuses from '@/constants/mapOrderStatuses';
	import AppTooltip from '@/components/app/AppTooltip.vue';
	import CountdownTimer from '@/components/orders/CountdownTimer.vue';
	import { statuses } from '@/constants/mapOrderStatuses';
	import AppIcon from '@/components/app/AppIcon.vue';

	export default {
		name: 'orderStatusesCard',
		components: {
			AppIcon,
			CountdownTimer,
			AppTooltip,
			AppModal,
			OrderStatusIcon,
			OrderStatusProgressBar,
		},
		emits: ['click', 'onStatusChange', 'deliverOrder', 'cookOrder', 'answer'],
		props: {
			order: {
				type: Object,
				required: true,
			},
			label: {
				type: String,
				default: null,
			},
			isSelected: {
				type: Boolean,
				default: false,
			},
		},
		data: () => ({
			test: '',
			names: names,
			waitersModal: false,
			waitersList: [],
			loading: false,
			selectedWaiter: null,
			orderStatuses: mapOrderStatuses,
			statuses: statuses,
			sound: null,
			soundPlayed: false,
		}),
		computed: {
			...mapGetters({
				posSettings: 'posSettings',
				user: 'user',
				loadingOrderId: 'loadingOrderId',
			}),
			isManagingOneBusiness() {
				return this.user?.restaurants?.length === 1;
			},
			isLoading() {
				return this.loadingOrderId === this.order.orderID;
			},
			delay() {
				let ms = moment().diff(moment(this.order.lastDateAccepted));
				let d = moment.duration(ms);
				let addMinutes = 0;
				if (this.order.status === 'DELIVERED') {
					addMinutes = 10;
				}
				return d.minutes() - addMinutes - this.order.deliverytime;
			},
			formatOrderId() {
				return OrderService.formatOrderId(this.order.orderID);
			},
			orderStatus() {
				return this.order.isTakeAwayOrder
					? 'Takeaway'
					: orders.status.find((status) => status.propValue === this.order.status).title;
			},
			statusTime() {
				const attrName = orders.status.find((status) => status.propValue === this.order.status).timeProp;
				return moment(this.order[attrName]).format('HH:mm');
			},
			getActiveStatusColor() {
				let orderStatus = this.orderStatuses.find((_status) => _status.value === this.order.status);
				if (orderStatus.value === 'REJECTED') {
					return '';
				}
				return orderStatus.color;
			},
			isRemainingTimeCritical() {
				if (!this.order.enableCookingTime || this.order.status !== this.statuses.accepted) {
					return 0;
				}
				const remainingSeconds = moment(this.order.lastDateAccepted)
					.add(this.order.totalCookingTime, 'minutes')
					.diff(moment(), 'seconds');

				return Math.floor(remainingSeconds / 60) <= (this.posSettings.reminderTimeInMin || 5); // Check if remaining time is less than 5 minutes
			},
			statusCardStyle() {
				if (this.isManagingOneBusiness) {
					return `status-card ${
						this.isRemainingTimeCritical
							? 'border-2 border-red-600 rounded-b-none'
							: 'border-1 border-p-light '
					}`;
				} else {
					return `relative flex shadow-sm gap-1 ${
						this.isRemainingTimeCritical
							? 'border-2 border-red-600'
							: 'border-1 border-p-light rounded-b-lg'
					}`;
				}
			},
		},
		methods: {
			onNotesClick(order) {
				this.emitter.emit('onNewOrderReceived', { orderId: order.orderID, isDumb: true });
			},
			encrypt(id) {
				return UtilityService.encrypt(id);
			},
			showWaitersModal() {
				this.getWaitersList();
				this.waitersModal = true;
			},
			getWaitersList() {
				this.loading = true;
				OrderService.getWaitersList(this.order.restaurant.restaurantID)
					.then((response) => {
						this.waitersList = response;
					})
					.catch((e) => {
						this.$notificationService.error(e);
					})
					.finally(() => {
						this.loading = false;
					});
			},
			assignWaiterToOrder(waiterId) {
				this.loading = true;
				OrderService.assignWaiterToOrder(this.order.orderID, waiterId)
					.then(() => {
						this.$notificationService.success('Waiter assigned successfully');
						this.waitersModal = false;
						this.$router.go();
					})
					.catch((e) => {
						this.$notificationService.error(e);
					})
					.finally(() => {
						this.loading = false;
					});
			},
			routeToOrderDetails() {
				this.$router.push({ name: RouteNames.OrderDetails, params: { id: this.encrypt(this.order.orderID) } });
			},
		},
	};
</script>

<style lang="scss" scoped></style>
